import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { deleteOrder, findAllOrderList } from "./services/order.services";
import {
  clearFilterOrder,
  clearOrderDetail,
  setFilterOrder,
  setOrderID,
} from "./orderSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import {
  checkAcessRights,
  formatToIndianCurrency,
  formatWeight,
} from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { findAllActivePurity } from "../purity/services/purity.services";
import { IPurity } from "../purity/purityModel";
import { FaEye } from "react-icons/fa";

const Order: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(findAllActivePurity());
    return () => {
      dispatch(closeDialog());
      dispatch(clearOrderDetail());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const handleOrderCreate = () => {
    navigate("/order/create");
  };
  return (
    <>
      <DeleteOrderDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Order List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleOrderCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <OrderGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const OrderGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const OrderList = useAppSelector((state) => state.order.OrderList);
  const loading = useAppSelector((state) => state.order.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const FilterOrder = useAppSelector((state) => state.order.FilterOrder);
  const PurityList = useAppSelector((state) => state.purity.PurityList);
  const [purityMap, setPurityMap] = useState<Map<number, IPurity>>(new Map());

  useEffect(() => {
    if (PurityList && PurityList?.length > 0) {
      const purityObjectMap = new Map<number, IPurity>();
      PurityList.forEach((item: IPurity) => {
        if (item?.ID) {
          purityObjectMap.set(item.ID, item);
        }
      });
      setPurityMap(purityObjectMap);
    }
  }, [PurityList]);

  useEffect(() => {
    const payload = {
      ...FilterOrder,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(findAllOrderList(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const MyEditCommandCell = (props: any) => {
    const handleEditItem = (ID: number) => {
      navigate("/order/edit", {
        state: { ID, order_guid: props.dataItem?.order_guid },
      });
    };

    const handlePrint = (order_guid: string) => {
      window.open(`/order/${order_guid}`, "_blank");
    };

    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteOrder"));
      dispatch(setOrderID(ID));
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            themeColor="success"
            onClick={() => handleEditItem(props.dataItem.order_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() => handleOpenDeleteDialog(props.dataItem.order_guid)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}

        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            themeColor="tertiary"
            onClick={() => handlePrint(props.dataItem.order_guid)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </td>
    );
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const OrderFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.order.FilterOrder}
        setFilterAction={setFilterOrder}
        clearFilterAction={clearFilterOrder}
        fetchDataAction={findAllOrderList}
        pagination={{ case: "lowercase" }}
      />
    ),
    [],
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        scrollable="scrollable"
        className="responsive-table"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={filter ? filterBy(OrderList, filter) : OrderList}
        total={OrderList[0]?.itemcount || OrderList.length}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          width={100}
          filterable={false}
          field="order_no"
          title="Order No"
          cells={{
            data: (props: any) => (
              <td>
                <div
                  className="hoverableName"
                  onClick={() =>
                    navigate("/order/edit", {
                      state: { ID: props.dataItem?.order_guid },
                    })
                  }
                >
                  {props.dataItem?.order_no}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="order_date"
          title="Order Date"
          width={130}
          cells={{ filterCell: OrderFilterCell }}
        />
        <Column
          field="delivery_date"
          title="Delivery Date"
          width={130}
          cells={{ filterCell: OrderFilterCell }}
        />
        <Column
          field="party_name"
          title="Vepari Name"
          width={200}
          cells={{ filterCell: OrderFilterCell }}
        />
        <Column
          field="item_name"
          title="Product Name"
          width={200}
          cells={{ filterCell: OrderFilterCell }}
        />
        <Column
          field="estimate_weight"
          title="Estimate Weight (gm)"
          width={190}
          cells={{
            data: (props: any) => (
              <td>
                <div>
                  <Typography.h6 style={{ margin: 0, textAlign: "end" }}>
                    {formatWeight(props.dataItem?.estimate_weight)}
                  </Typography.h6>
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="Carat"
          title="Purity"
          width={150}
          cells={{
            data: (props: any) => (
              <td>
                <div>
                  <Typography.h6 style={{ margin: 0 }}>
                    {`${
                      purityMap.get(props.dataItem?.purity)?.Carat || ""
                    } -  ${
                      purityMap.get(props.dataItem?.purity)?.purity || ""
                    }`}
                  </Typography.h6>
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="color"
          title="Color"
          width={150}
          cells={{ filterCell: OrderFilterCell }}
        />
        <Column
          field="estimate_amount"
          title="Estimated Amount (Rs.)"
          width={200}
          cells={{
            data: (props: any) => (
              <td>
                <div>
                  <Typography.h6 style={{ margin: 0, textAlign: "end" }}>
                    {formatToIndianCurrency(props.dataItem?.estimate_amount)}
                  </Typography.h6>
                </div>
              </td>
            ),
          }}
        />
        {(isDeleteAccess || isEditAccess) && (
          <Column
            field="order_guid"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={"150px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteOrderDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const OrderID = useAppSelector((state) => state.order.OrderID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterOrder = useAppSelector((state) => state.order.FilterOrder);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteOrder(ID));
      dispatch(setCurrentPage(0));
      const payload = {
        ...FilterOrder,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.payload?.Data.Status === 200) {
        dispatch(findAllOrderList(payload));
        dispatch(closeDialog());
        dispatch(clearOrderDetail());
      } else {
        dispatch(findAllOrderList(payload));
        dispatch(closeDialog());
        dispatch(clearOrderDetail());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteOrder" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Order?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Order?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(OrderID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Order;
